import { useEffect, useRef, useState, type FC } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { TourStepData } from '@/lib/tour-data/types';

export type { TourStepData };

interface TourStepProps {
  step: TourStepData;
  current: number;
  total: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

const OFFSET = 12;

export const TourStep: FC<TourStepProps> = ({ step, current, total, onNext, onPrev, onSkip }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const el = document.querySelector(step.target);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const tooltip = ref.current;
    const tw = tooltip?.offsetWidth ?? 320;
    const th = tooltip?.offsetHeight ?? 180;

    let top = 0;
    let left = 0;

    switch (step.placement) {
      case 'bottom':
        top = rect.bottom + OFFSET;
        left = rect.left + rect.width / 2 - tw / 2;
        break;
      case 'top':
        top = rect.top - th - OFFSET;
        left = rect.left + rect.width / 2 - tw / 2;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - th / 2;
        left = rect.right + OFFSET;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - th / 2;
        left = rect.left - tw - OFFSET;
        break;
    }

    // Clamp to viewport
    left = Math.max(8, Math.min(left, window.innerWidth - tw - 8));
    top = Math.max(8, Math.min(top, window.innerHeight - th - 8));

    setPos({ top, left });
  }, [step]);

  return (
    <div
      ref={ref}
      className="fixed z-[10002] w-80 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      style={{ top: pos.top, left: pos.left }}
    >
      {/* Close */}
      <button
        onClick={onSkip}
        className="absolute top-2 right-2 p-1 rounded-md text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="p-4">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white pr-6">
          {step.title}
        </h3>
        <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
          {step.content}
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-700 px-4 py-2.5">
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
          {current + 1} / {total}
        </span>
        <div className="flex items-center gap-1.5">
          {current > 0 && (
            <button
              onClick={onPrev}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <ChevronLeft className="w-3 h-3" />
              Prec.
            </button>
          )}
          <button
            onClick={onNext}
            className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
          >
            {current === total - 1 ? 'Terminer' : 'Suivant'}
            {current < total - 1 && <ChevronRight className="w-3 h-3" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourStep;
